import { useMemo, useState } from 'react';
import { Content, Header, Page, Progress } from '@backstage/core-components';
import { Typography, makeStyles } from '@material-ui/core';
import { outcomeOf } from '../catalog/pipeline/outcome';
import { useInsights } from '../catalog/pipeline/useInsights';
import { DeploysList } from './DeploysList';
import { FiguresBar } from './FiguresBar';
import { figuresOf, filterDeploys, type EnvironmentChoice } from './figures';
import type { DeploymentsSnapshot } from './snapshot';
import { useServiceNames } from './useServiceNames';

const ENVIRONMENT_CHIPS: { id: EnvironmentChoice; label: string }[] = [
  { id: 'all', label: 'All environments' },
  { id: 'production', label: 'production' },
  { id: 'qm', label: 'qm' },
  { id: 'test', label: 'test' },
];

const useStyles = makeStyles(theme => ({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    minWidth: 0,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 30,
    padding: theme.spacing(0, 1.25),
    borderRadius: 'var(--radius-pill)',
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
    color: 'var(--ui-text)',
    font: 'inherit',
    fontSize: '0.8125rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: 'var(--ui-primary)' },
    '&:focus-visible': {
      outline: '2px solid var(--ui-primary)',
      outlineOffset: 1,
    },
    '&[aria-pressed="true"]': {
      borderColor: 'var(--ui-primary)',
      background: 'var(--ui-nav-active-bg)',
      color: 'var(--ui-primary)',
      fontWeight: 600,
    },
  },
  list: {
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    padding: theme.spacing(0.5, 2),
    minWidth: 0,
  },
  message: { color: 'var(--ui-text-muted)', padding: theme.spacing(2, 0) },
}));

/**
 * The Deployments page (DX-304; promised in specs/deployments.md): every
 * repository's deploys of the week, where each went, and four figures that say
 * what they count.
 *
 * It reads the same snapshot as the home page's Recent deployments card, which
 * the github-insights backend computes from the workflow runs its CI poll
 * already pages — so the page costs no GitHub calls of its own, and it and the
 * card cannot disagree about what was deployed.
 *
 * Three states say what they are rather than showing figures: the snapshot not
 * collected yet, the snapshot not loaded, and a week without deploys. A zero
 * is a measurement, and only the last of the three has made one.
 *
 * There is no Deploy action. The portal cannot deploy, and a component's own
 * page already leads to its deploy workflow.
 */
export const DeploymentsPage = () => {
  const classes = useStyles();
  // Asked again every minute while a deploy on it is still running, so its row
  // does not read "Running" until somebody reloads. It costs no GitHub calls:
  // the snapshot is served from the collection the backend already made.
  const snapshot = useInsights<DeploymentsSnapshot>('/deployments', {
    refreshWhile: data =>
      data.deployments.some(row => outcomeOf(row).kind === 'running'),
  });
  const services = useServiceNames();
  const [environment, setEnvironment] = useState<EnvironmentChoice>('all');
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [hideCustomerProjects, setHideCustomerProjects] = useState(false);

  const rows = useMemo(() => snapshot.data?.deployments ?? [], [snapshot.data]);
  const figures = useMemo(() => figuresOf(rows), [rows]);
  const shown = useMemo(
    () =>
      filterDeploys(rows, { environment, onlyFailed, hideCustomerProjects }),
    [rows, environment, onlyFailed, hideCustomerProjects],
  );

  let body: JSX.Element;
  if (snapshot.loading) {
    body = <Progress />;
  } else if (snapshot.error || !snapshot.data) {
    body = (
      <Typography role="alert">
        Couldn't load the deployments from github-insights.
      </Typography>
    );
  } else if (snapshot.data.generatedAt === null) {
    body = (
      <Typography className={classes.message}>
        The first deployment snapshot is still being collected. Check back in a
        moment.
      </Typography>
    );
  } else {
    const { windowDays, generatedAt } = snapshot.data;
    let list: JSX.Element;
    if (!rows.length) {
      list = (
        <Typography className={classes.message}>
          No deploys in the last {windowDays} days. Most repositories here do
          not deploy — libraries and SDKs publish packages instead, which this
          page does not count.
        </Typography>
      );
    } else if (!shown.length) {
      list = (
        <Typography className={classes.message}>
          None of the {rows.length} deploys of the last {windowDays} days match
          these filters.
        </Typography>
      );
    } else {
      list = (
        <DeploysList
          key={`${environment}:${onlyFailed}:${hideCustomerProjects}`}
          deploys={shown}
          services={services}
          source={generatedAt}
        />
      );
    }

    body = (
      <div className={classes.stack}>
        <FiguresBar figures={figures} windowDays={windowDays} />
        {rows.length > 0 && (
          <div className={classes.chips}>
            <div
              className={classes.chips}
              role="group"
              aria-label="Environment"
            >
              {ENVIRONMENT_CHIPS.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  className={classes.chip}
                  aria-pressed={environment === id}
                  onClick={() => setEnvironment(id)}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={classes.chip}
              aria-pressed={onlyFailed}
              onClick={() => setOnlyFailed(on => !on)}
            >
              Failed
            </button>
            {/* Only where there is one to hide: a chip that never changes the
                list reads as a filter that is broken. */}
            {rows.some(row => row.customerProject) && (
              <button
                type="button"
                className={classes.chip}
                aria-pressed={hideCustomerProjects}
                onClick={() => setHideCustomerProjects(on => !on)}
              >
                Hide customer projects
              </button>
            )}
          </div>
        )}
        <div className={classes.list}>{list}</div>
      </div>
    );
  }

  return (
    <Page themeId="home">
      <Header
        title="Deployments"
        subtitle="Production and pre-production deploys across all repositories."
      />
      <Content>{body}</Content>
    </Page>
  );
};
